import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Building2, Sparkles, Phone, ArrowRight, ChevronDown, UtensilsCrossed, Shirt, Flame, Refrigerator, Droplets, Blinds, Trash2, Fence } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import { ServiceNav } from '../components/ServiceNav';
const cardClass = 'bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-sm';
const stepHeadingClass = 'font-bebas text-2xl md:text-3xl text-[#1a2744] pb-3 border-b border-gray-200';
const labelClass = 'text-[#1a2744] font-medium text-sm';
const inputClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all placeholder-gray-400';
const selectClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all appearance-none cursor-pointer';
type PillGroupProps = {
  options: string[];
  selected: string | string[];
  onSelect: (val: string) => void;
  multi?: boolean;
};
function PillGroup({
  options,
  selected,
  onSelect,
  multi = false
}: PillGroupProps) {
  const isSelected = (opt: string) => multi ? (selected as string[]).includes(opt) : selected === opt;
  return <div className="flex flex-wrap gap-3" data-id="element-1486">
      {options.map(opt => <button key={opt} type="button" onClick={() => onSelect(opt)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${isSelected(opt) ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-1487">
          {opt}
        </button>)}
    </div>;
}
const extras = [{
  name: 'Washing dishes',
  price: 7,
  icon: UtensilsCrossed
}, {
  name: 'Laundry (wash & fold)',
  price: 7,
  icon: Shirt
}, {
  name: 'Inside of oven',
  price: 7,
  icon: Flame
}, {
  name: 'Inside of fridge',
  price: 7,
  icon: Refrigerator
}, {
  name: 'Flu Buster - Eucalyptus',
  price: 7,
  icon: Droplets
}, {
  name: 'Wet Wipe Blinds',
  price: 7,
  icon: Blinds
}, {
  name: 'Property Cleanup',
  price: 7,
  icon: Trash2
}, {
  name: 'Patio or Balcony',
  price: 7,
  icon: Fence
}];
const timeSlots = ['14:00', '15:00'];
export function SpringDeepCleaningQuote() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Step 1 - Contact
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({
      ...contact,
      [e.target.name]: e.target.value
    });
  };
  // Step 2 - Location
  const [location, setLocation] = useState({
    address: '',
    unit: '',
    city: '',
    postalCode: ''
  });
  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation({
      ...location,
      [e.target.name]: e.target.value
    });
  };
  // Step 3 - Cleaning details
  const [hours, setHours] = useState('2');
  const [cleaners, setCleaners] = useState('1');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [additionalRooms, setAdditionalRooms] = useState('');
  const [floors, setFloors] = useState('');
  const [parking, setParking] = useState('');
  const [pets, setPets] = useState<string[]>(['None']);
  // Step 4 - Extras
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const toggleExtra = (name: string) => {
    setSelectedExtras(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);
  };
  // Step 5 - Frequency
  const [frequency, setFrequency] = useState('');
  // Step 6 - Date & Time
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const handlePetSelect = (pet: string) => {
    if (pet === 'None') {
      setPets(['None']);
    } else {
      setPets(prev => {
        const withoutNone = prev.filter(p => p !== 'None');
        if (withoutNone.includes(pet)) {
          const result = withoutNone.filter(p => p !== pet);
          return result.length === 0 ? ['None'] : result;
        }
        return [...withoutNone, pet];
      });
    }
  };
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-1488">
      <SEOHead title="Book Spring / Deep Cleaning | Smith & Jengs Cleaning Services" description="Book your deep or spring cleaning service in Bicester and Oxfordshire. Thorough top-to-bottom cleaning, online booking in 60 seconds." canonical="https://smithandjengs.com/spring-deep-cleaning-quote" data-id="element-1489" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Spring / Deep Cleaning',
      url: 'https://smithandjengs.com/spring-deep-cleaning'
    }, {
      name: 'Book Online',
      url: 'https://smithandjengs.com/spring-deep-cleaning-quote'
    }]} data-id="element-1490" />
      <Navbar data-id="element-1491" />

      <main className="flex-1 pt-24" data-id="element-1492">
        <ServiceNav data-id="element-1493" />

        {/* Header */}
        <section className="w-full bg-white pt-12 md:pt-16 pb-12 px-4 md:px-12" data-id="element-1494">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl w-full mx-auto text-center" data-id="element-1495">
            <h1 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-6" data-id="element-1496">
              <span className="text-5xl md:text-6xl lg:text-7xl block" data-id="element-1497">
                BOOK ONLINE
              </span>
              <span className="text-3xl md:text-4xl lg:text-5xl block text-[#e91e8c]" data-id="element-1498">
                SPRING / DEEP CLEANING
              </span>
            </h1>
            <p className="text-gray-600 font-medium max-w-2xl mx-auto text-lg" data-id="element-1499">
              It only takes 60 seconds to book your deep home cleaning. Fill in
              the details below and we'll take care of the rest.
            </p>
          </motion.div>
        </section>

        {/* Form Section */}
        <section className="w-full bg-[#f8f9fa] py-12 md:py-16 px-4 md:px-8" id="booking-form" data-id="element-1500">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-4xl w-full mx-auto" data-id="element-1501">
            <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-1502">
              {/* STEP 1: Contact */}
              <div className={cardClass} data-id="element-1503">
                <h3 className={stepHeadingClass} data-id="element-1504">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1505">STEP 1:</span> Contact
                  Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1506">
                  <div className="space-y-2" data-id="element-1507">
                    <label className={labelClass} data-id="element-1508">First Name</label>
                    <input type="text" name="firstName" value={contact.firstName} onChange={handleContactChange} placeholder="First Name" className={inputClass} data-id="element-1509" />
                  </div>
                  <div className="space-y-2" data-id="element-1510">
                    <label className={labelClass} data-id="element-1511">Last Name</label>
                    <input type="text" name="lastName" value={contact.lastName} onChange={handleContactChange} placeholder="Last Name" className={inputClass} data-id="element-1512" />
                  </div>
                  <div className="space-y-2" data-id="element-1513">
                    <label className={labelClass} data-id="element-1514">Email</label>
                    <input type="email" name="email" value={contact.email} onChange={handleContactChange} placeholder="Email Address" className={inputClass} data-id="element-1515" />
                  </div>
                  <div className="space-y-2" data-id="element-1516">
                    <label className={labelClass} data-id="element-1517">Phone Number</label>
                    <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} placeholder="Phone Number" className={inputClass} data-id="element-1518" />
                  </div>
                </div>
              </div>

              {/* STEP 2: Location */}
              <div className={cardClass} data-id="element-1519">
                <h3 className={stepHeadingClass} data-id="element-1520">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1521">STEP 2:</span> Cleaning
                  Location
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1522">
                  <div className="space-y-2" data-id="element-1523">
                    <label className={labelClass} data-id="element-1524">Street Address</label>
                    <input type="text" name="address" value={location.address} onChange={handleLocationChange} placeholder="Enter a location" className={inputClass} data-id="element-1525" />
                  </div>
                  <div className="space-y-2" data-id="element-1526">
                    <label className={labelClass} data-id="element-1527">Unit/Apt/Suite #</label>
                    <input type="text" name="unit" value={location.unit} onChange={handleLocationChange} placeholder="Unit/Apt/Suite #" className={inputClass} data-id="element-1528" />
                  </div>
                  <div className="space-y-2" data-id="element-1529">
                    <label className={labelClass} data-id="element-1530">City</label>
                    <input type="text" name="city" value={location.city} onChange={handleLocationChange} placeholder="City" className={inputClass} data-id="element-1531" />
                  </div>
                  <div className="space-y-2" data-id="element-1532">
                    <label className={labelClass} data-id="element-1533">Postal Code</label>
                    <input type="text" name="postalCode" value={location.postalCode} onChange={handleLocationChange} placeholder="Postal Code" className={inputClass} data-id="element-1534" />
                  </div>
                </div>
              </div>

              {/* STEP 3: Cleaning Details */}
              <div className={cardClass} data-id="element-1535">
                <h3 className={stepHeadingClass} data-id="element-1536">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1537">STEP 3:</span> Tell Us
                  About the Cleaning
                </h3>
                <div className="space-y-6 mt-8" data-id="element-1538">
                  {/* Hours & Cleaners */}
                  <div data-id="element-1539">
                    <label className={`${labelClass} block mb-3`} data-id="element-1540">
                      How many hours and cleaners would you like to book?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1541">
                      <div className="relative" data-id="element-1542">
                        <select value={hours} onChange={e => setHours(e.target.value)} className={selectClass} data-id="element-1543">
                          {[2, 3, 4, 5, 6, 7, 8].map(h => <option key={h} value={String(h)} data-id="element-1544">
                              {h} Hours
                            </option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1545" />
                      </div>
                      <div className="relative" data-id="element-1546">
                        <select value={cleaners} onChange={e => setCleaners(e.target.value)} className={selectClass} data-id="element-1547">
                          {[1, 2, 3, 4].map(c => <option key={c} value={String(c)} data-id="element-1548">
                              {c} Cleaner{c > 1 ? 's' : ''}
                            </option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1549" />
                      </div>
                    </div>
                  </div>

                  {/* Bedrooms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1550">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1551">
                      Bedrooms
                    </label>
                    <div className="relative md:w-64" data-id="element-1552">
                      <select value={bedrooms} onChange={e => setBedrooms(e.target.value)} className={selectClass} data-id="element-1553">
                        <option value="" disabled data-id="element-1554">
                          Select
                        </option>
                        <option value="studio" data-id="element-1555">Studio / 1 Bedroom</option>
                        <option value="2" data-id="element-1556">2 Bedrooms</option>
                        <option value="3" data-id="element-1557">3 Bedrooms</option>
                        <option value="4" data-id="element-1558">4 Bedrooms</option>
                        <option value="5+" data-id="element-1559">5+ Bedrooms</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1560" />
                    </div>
                  </div>

                  {/* Bathrooms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1561">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1562">
                      Bathrooms
                    </label>
                    <div className="relative md:w-64" data-id="element-1563">
                      <select value={bathrooms} onChange={e => setBathrooms(e.target.value)} className={selectClass} data-id="element-1564">
                        <option value="" disabled data-id="element-1565">
                          Select
                        </option>
                        <option value="1" data-id="element-1566">1 Bathroom</option>
                        <option value="2" data-id="element-1567">2 Bathrooms</option>
                        <option value="3" data-id="element-1568">3 Bathrooms</option>
                        <option value="4+" data-id="element-1569">4+ Bathrooms</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1570" />
                    </div>
                  </div>

                  {/* Additional Rooms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1571">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1572">
                      Additional Rooms (dining room, living room, office, etc)
                    </label>
                    <div className="relative md:w-64" data-id="element-1573">
                      <select value={additionalRooms} onChange={e => setAdditionalRooms(e.target.value)} className={selectClass} data-id="element-1574">
                        <option value="" disabled data-id="element-1575">
                          Select
                        </option>
                        <option value="0" data-id="element-1576">No additional rooms</option>
                        <option value="1" data-id="element-1577">1</option>
                        <option value="2" data-id="element-1578">2</option>
                        <option value="3" data-id="element-1579">3</option>
                        <option value="4+" data-id="element-1580">4+</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1581" />
                    </div>
                  </div>

                  {/* Number of Floors */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1582">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1583">
                      Number of Floors (incl basement)
                    </label>
                    <div className="relative md:w-64" data-id="element-1584">
                      <select value={floors} onChange={e => setFloors(e.target.value)} className={selectClass} data-id="element-1585">
                        <option value="" disabled data-id="element-1586">
                          Select
                        </option>
                        <option value="1" data-id="element-1587">1 Floor</option>
                        <option value="2" data-id="element-1588">2 Floors</option>
                        <option value="3" data-id="element-1589">3 Floors</option>
                        <option value="4+" data-id="element-1590">4+ Floors</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1591" />
                    </div>
                  </div>

                  {/* Parking */}
                  <div className="space-y-3" data-id="element-1592">
                    <label className={labelClass} data-id="element-1593">Parking</label>
                    <PillGroup options={['Free on street', 'Free home parking', 'Parking meters', 'Paid parking lot']} selected={parking} onSelect={setParking} data-id="element-1594" />
                  </div>

                  {/* Pets */}
                  <div className="space-y-3" data-id="element-1595">
                    <label className={labelClass} data-id="element-1596">
                      Do you have any pets?{' '}
                      <span className="text-gray-400 font-normal" data-id="element-1597">
                        Required for safety & allergy reasons
                      </span>
                    </label>
                    <PillGroup options={['None', 'Dog', 'Cat']} selected={pets} onSelect={handlePetSelect} multi data-id="element-1598" />
                  </div>
                </div>
              </div>

              {/* STEP 4: Deep Cleaning Extras */}
              <div className={cardClass} data-id="element-1599">
                <h3 className={stepHeadingClass} data-id="element-1600">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1601">STEP 4:</span> Deep
                  Cleaning Extras
                </h3>
                <p className="text-sm text-gray-500 mt-3" data-id="element-1602">
                  Free up more of your precious time for what matters most.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-id="element-1603">
                  {extras.map(({
                  name,
                  price,
                  icon: IconComp
                }) => {
                  const isActive = selectedExtras.includes(name);
                  return <button key={name} type="button" onClick={() => toggleExtra(name)} className={`flex flex-col items-center text-center gap-3 p-5 rounded-xl border-2 transition-all ${isActive ? 'border-[#e91e8c] bg-[#e91e8c]/5 shadow-sm' : 'border-gray-200 bg-white hover:border-[#e91e8c]/40'}`} data-id="element-1604">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${isActive ? 'bg-[#e91e8c]/10 text-[#e91e8c]' : 'bg-[#f8f9fa] text-[#1a2744]'}`} data-id="element-1605">
                          <IconComp className="w-6 h-6" data-id="element-1606" />
                        </div>
                        <span className="text-xs font-medium text-[#1a2744] leading-tight" data-id="element-1607">
                          {name}
                        </span>
                        <span className={`text-xs font-semibold ${isActive ? 'text-[#e91e8c]' : 'text-gray-500'}`} data-id="element-1608">
                          £{price}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* STEP 5: Frequency */}
              <div className={cardClass} data-id="element-1609">
                <h3 className={stepHeadingClass} data-id="element-1610">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1611">STEP 5:</span> Select
                  Frequency
                </h3>
                <div className="mt-6" data-id="element-1612">
                  <PillGroup options={['One time', 'Quarterly']} selected={frequency} onSelect={setFrequency} data-id="element-1613" />
                </div>
              </div>

              {/* STEP 6: Date & Time */}
              <div className={cardClass} data-id="element-1614">
                <h3 className={stepHeadingClass} data-id="element-1615">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1616">STEP 6:</span> Date of
                  Service
                </h3>
                <p className="text-sm text-gray-500 mt-3" data-id="element-1617">
                  For recurring services, each one will commence on the same day
                  and time as selected for the first service.
                </p>
                <div className="space-y-6 mt-8" data-id="element-1618">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1619">
                    <div className="space-y-2" data-id="element-1620">
                      <label className={labelClass} data-id="element-1621">Select a date</label>
                      <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-id="element-1622" />
                    </div>
                    <div className="space-y-2" data-id="element-1623">
                      <label className={labelClass} data-id="element-1624">Select a time slot</label>
                      <div className="flex gap-3" data-id="element-1625">
                        {timeSlots.map(slot => <button key={slot} type="button" onClick={() => setTimeSlot(slot)} className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all border ${timeSlot === slot ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-1626">
                            {slot}
                          </button>)}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400" data-id="element-1627">
                    For same day bookings, a customer service agent will call
                    you.
                  </p>
                  <div className="space-y-2" data-id="element-1628">
                    <label className={labelClass} data-id="element-1629">
                      Are there any additional details we need to know?
                    </label>
                    <textarea value={additionalDetails} onChange={e => setAdditionalDetails(e.target.value)} placeholder="Any special instructions or details..." rows={4} className="w-full border border-gray-200 rounded-lg p-4 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all resize-none placeholder-gray-400" data-id="element-1630" />
                  </div>
                </div>
              </div>

              {/* STEP 7: Submit */}
              <div className={cardClass} data-id="element-1631">
                <div className="text-center space-y-4" data-id="element-1632">
                  <h3 className="font-bebas text-2xl md:text-3xl text-[#1a2744]" data-id="element-1633">
                    Ready to Book?
                  </h3>
                  <p className="text-gray-500 text-sm max-w-md mx-auto" data-id="element-1634">
                    Review your details above and click below to confirm your
                    deep cleaning booking. We'll send you a confirmation email
                    right away.
                  </p>
                  <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-12 py-4 rounded-full font-semibold tracking-wider text-lg transition-colors shadow-lg hover:shadow-xl" data-id="element-1635">
                    BOOK NOW
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </section>

        {/* Explore Our Services */}
        <section className="w-full bg-white py-20 px-4 md:px-12 border-t border-gray-200" data-id="element-1636">
          <div className="max-w-7xl mx-auto" data-id="element-1637">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-1638">
              <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-[#1a2744] tracking-wide" data-id="element-1639">
                EXPLORE OUR <span className="text-[#e91e8c]" data-id="element-1640">SERVICES</span>
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto" data-id="element-1641">
                Discover our full range of professional cleaning solutions
                tailored to your needs.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1642">
              {[{
              title: 'Basic Home Cleaning',
              desc: 'Regular maintenance to keep your home spotless.',
              link: '/basic-home-cleaning',
              icon: Home
            }, {
              title: 'Spring / Deep Cleaning',
              desc: 'Thorough, top-to-bottom detail cleaning.',
              link: '/spring-deep-cleaning',
              icon: Sparkles
            }, {
              title: 'Commercial Cleaning',
              desc: 'Professional cleaning for your business.',
              link: '/commercial-cleaning',
              icon: Building2
            }, {
              title: 'Contact Us',
              desc: 'Get in touch for custom requests.',
              link: '/contact-us',
              icon: Phone
            }].map((service, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} data-id="element-1643">
                  <Link to={service.link} className="group block h-full bg-[#f8f9fa] rounded-xl p-8 hover:shadow-md transition-all border border-gray-100 hover:border-[#5bbcd5]" data-id="element-1644">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-6 group-hover:bg-[#5bbcd5]/10 transition-colors" data-id="element-1645">
                      <service.icon className="w-6 h-6 text-[#1a2744] group-hover:text-[#5bbcd5] transition-colors" data-id="element-1646" />
                    </div>
                    <h3 className="font-bebas text-2xl text-[#1a2744] mb-3 tracking-wide group-hover:text-[#e91e8c] transition-colors" data-id="element-1647">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6 line-clamp-2" data-id="element-1648">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-[#5bbcd5] font-medium text-sm mt-auto" data-id="element-1649">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-1650" />
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1651" />
    </div>;
}